import { useState } from "react";
import { ChevronDown, Loader2, Mail, MapPin, HelpCircle } from "lucide-react";
import { toast } from "sonner";
import { FadeIn, Eyebrow } from "@/components/Reveal";
import { FAQS } from "@/data/site";
import { api } from "@/lib/api";
import Seo from "@/components/Seo";

const inputCls =
  "w-full bg-white border border-cloud px-4 py-3 text-sm focus:outline-none focus:border-teal transition-colors";
const labelCls = "block text-xs font-mono uppercase tracking-[0.16em] text-teal mb-2";

const CATEGORIES = [
  "Toolkit question",
  "Consultation enquiry",
  "Planning services (upcoming)",
  "Media / speaking",
  "Something else",
];

const Contact = () => {
  const [busy, setBusy] = useState(false);
  const [open, setOpen] = useState(0);
  const [form, setForm] = useState({
    name: "",
    email: "",
    phone: "",
    category: CATEGORIES[0],
    suburb: "",
    message: "",
  });
  const set = (k) => (e) => setForm({ ...form, [k]: e.target.value });

  const submit = async (e) => {
    e.preventDefault();
    if (busy) return;
    setBusy(true);
    try {
      const { data } = await api.post("/contact", form);
      toast.success(`Enquiry received — reference ${data.enquiry_ref}. We'll reply within one business day.`);
      setForm({ name: "", email: "", phone: "", category: CATEGORIES[0], suburb: "", message: "" });
    } catch {
      toast.error("Your enquiry could not be sent. Please try again.");
    } finally {
      setBusy(false);
    }
  };

  return (
    <div data-testid="contact-page">
      <Seo
        title="Contact Us"
        description="Questions about a toolkit, a booking or your NSW property project? Send an enquiry to PLAN FIRST NSW — plus answers to common planning questions."
      />
      <section className="bg-sand-light py-20 lg:py-24">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <Eyebrow>Contact &amp; Support</Eyebrow>
          <h1 className="mt-4 font-serif text-3xl sm:text-4xl lg:text-5xl tracking-tight text-teal">
            Not sure where to start? Start here.
          </h1>
          <p className="mt-5 max-w-2xl text-charcoal/70 leading-relaxed">
            Ask a question, check whether a toolkit fits your situation, or tell us about your
            project. Every enquiry gets a genuine reply — usually within one business day.
          </p>
        </div>
      </section>

      <section className="py-16 lg:py-24">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 grid lg:grid-cols-12 gap-12">
          <div className="lg:col-span-7">
            <FadeIn>
              <h2 className="font-serif text-2xl text-teal tracking-tight">Send an enquiry</h2>
              <form onSubmit={submit} className="mt-8 grid sm:grid-cols-2 gap-5" data-testid="contact-form">
                <div>
                  <label className={labelCls}>Full name *</label>
                  <input required className={inputCls} value={form.name} onChange={set("name")} data-testid="contact-name-input" />
                </div>
                <div>
                  <label className={labelCls}>Email *</label>
                  <input required type="email" className={inputCls} value={form.email} onChange={set("email")} data-testid="contact-email-input" />
                </div>
                <div>
                  <label className={labelCls}>Phone</label>
                  <input className={inputCls} value={form.phone} onChange={set("phone")} data-testid="contact-phone-input" />
                </div>
                <div>
                  <label className={labelCls}>Enquiry type *</label>
                  <select className={inputCls} value={form.category} onChange={set("category")} data-testid="contact-category-select">
                    {CATEGORIES.map((c) => (
                      <option key={c}>{c}</option>
                    ))}
                  </select>
                </div>
                <div className="sm:col-span-2">
                  <label className={labelCls}>Suburb / council area</label>
                  <input className={inputCls} value={form.suburb} onChange={set("suburb")} placeholder="Helps us point you to the right local rules" data-testid="contact-suburb-input" />
                </div>
                <div className="sm:col-span-2">
                  <label className={labelCls}>Your message *</label>
                  <textarea required className={`${inputCls} min-h-[140px]`} value={form.message} onChange={set("message")} data-testid="contact-message-input" />
                </div>
                <div className="sm:col-span-2">
                  <button
                    type="submit"
                    disabled={busy}
                    data-testid="contact-form-submit-button"
                    className="inline-flex items-center gap-2 bg-teal text-ivory font-medium px-8 py-3.5 rounded-full hover:bg-teal-dark transition-colors disabled:opacity-60"
                  >
                    {busy && <Loader2 className="w-4 h-4 animate-spin" />}
                    Send enquiry
                  </button>
                </div>
              </form>
            </FadeIn>
          </div>

          <div className="lg:col-span-5">
            <FadeIn delay={0.1}>
              <div className="bg-white border border-cloud p-8">
                <h3 className="font-serif text-xl text-teal">Business details</h3>
                <ul className="mt-5 space-y-4 text-sm text-charcoal/75">
                  <li className="flex gap-3">
                    <Mail className="w-4 h-4 text-teal mt-0.5 shrink-0" />
                    hello@planfirstnsw.com.au
                  </li>
                  <li className="flex gap-3">
                    <MapPin className="w-4 h-4 text-teal mt-0.5 shrink-0" />
                    Sydney, New South Wales — serving all 128 NSW councils online
                  </li>
                  <li className="flex gap-3">
                    <HelpCircle className="w-4 h-4 text-teal mt-0.5 shrink-0" />
                    Independent private service — not a government or council platform
                  </li>
                </ul>
              </div>
            </FadeIn>
          </div>
        </div>

        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 mt-20">
          <FadeIn>
            <Eyebrow>Frequently Asked</Eyebrow>
            <h2 className="mt-4 font-serif text-2xl sm:text-3xl tracking-tight text-teal">
              Answers before you even ask
            </h2>
          </FadeIn>
          <div className="mt-8 border-t border-cloud" data-testid="faq-list">
            {FAQS.map((f, i) => (
              <div key={f.q} className="border-b border-cloud" data-testid={`contact-faq-${i}`}>
                <button
                  className="w-full flex items-center justify-between gap-4 py-6 text-left"
                  onClick={() => setOpen(open === i ? -1 : i)}
                >
                  <span className="font-serif text-lg text-teal">{f.q}</span>
                  <ChevronDown
                    className={`w-5 h-5 text-teal shrink-0 transition-transform duration-300 ${
                      open === i ? "rotate-180" : ""
                    }`}
                  />
                </button>
                {open === i && (
                  <p className="pb-6 text-charcoal/75 leading-relaxed max-w-3xl">{f.a}</p>
                )}
              </div>
            ))}
          </div>
        </div>
      </section>
    </div>
  );
};

export default Contact;
